// Kořen okenního frameworku — „plocha" (desktop), na které žijí okna.
//
// DŮLEŽITÉ: všechna okna renderujeme v JEDNOM keyed seznamu ve STABILNÍM lineárním
// pořadí (navigation.layers). Detach/dock/maximize/minimize i přechod list↔detail mění
// jen styl (pozice, z-index, display) — nikdy pozici v DOM → iframe legacy agend se
// NEREMOUNTUJE. Stacking řeší z-index per STACK; kliknutí přenese celý stack do popředí.
//
// List-detail: root agenda s layoutMode 'list-detail' rozloží docked okna na list (vlevo)
// a viditelný detail (vpravo, oddělené splitterem). Detail je plnohodnotné okno — po
// osamostatnění (detach) jen změní rect na floating (stejný DOM uzel, žádný reload).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  useNavigationContext,
  useNavigationStore,
} from '@/shared/navigation/react/useNavigationContext';
import {
  NavigationErrorLayer,
  NavigationLayerHost,
  NavigationLazyPlaceholder,
} from '@/shared/navigation/shell';

import { Icon } from '@/shared/ui/icon';

import { deriveDesktopProjection, type DesktopWindow } from '../core/deriveDesktopProjection';
import { snapRectForZone } from '../core/snap';
import { useSnapStore } from '../core/snapStore';
import {
  clampRectKeepTitle,
  clampRectToViewport,
  stackKeyOf,
  type WindowRect,
} from '../core/windowManagerTypes';
import { useWindowManagerStore } from '../core/windowManagerStore';
import { readWindowSizeMemory } from '../core/windowSizeMemory';

import { useLegacyWindowChrome } from '../legacy/useLegacyWindowChrome';
import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';

import { PhoenixWindow } from './PhoenixWindow';
import { useWindowingActions } from './useWindowingActions';
import { usePointerDrag } from './usePointerDrag';
import { useWindowLayoutUrlHash } from './useWindowLayoutUrlHash';

/** Výchozí odsazení kaskády v ploše (px). 0 = okna kryjí celou plochu (Spec scéna 1). */
const DOCK_OFFSET_PX = 0;
/** Násobitel z-ranku stacku (mezera pro okna v rámci stacku). */
const STACK_Z_SPAN = 100;
/** Výchozí / mezní šířka list panelu (list-detail). */
const DEFAULT_LIST_W = 360;
const MIN_LIST_W = 320; // = .pwm-win min-width (jinak by panel přetekl přes splitter)
const MIN_DETAIL_W = 320;
/** Výška dvouřádkové hlavičky Phoenix okna (crumbrow 22 + titlerow 34 + 2× border ≈ 58 px).
 *  Přičítáme k reportované výšce legacy dialogu, ať iframe (obsah) dostane ~reportovanou výšku. */
const WIN_HEADER_PX = 58;

function splitterStorageKey(agendaId: string): string {
  return `nav:splitter:${agendaId}`;
}

export function PhoenixDesktop() {
  const { kernel } = useNavigationContext();
  const navigation = useNavigationStore((s) => s.navigation);

  const windows = useWindowManagerStore((s) => s.windows);
  const stackZ = useWindowManagerStore((s) => s.stackZ);
  const activeLayerId = useWindowManagerStore((s) => s.activeLayerId);
  const ensureDefaults = useWindowManagerStore((s) => s.ensureDefaults);
  const ensureStacks = useWindowManagerStore((s) => s.ensureStacks);
  const prune = useWindowManagerStore((s) => s.prune);
  const setRect = useWindowManagerStore((s) => s.setRect);
  const setDesktopSize = useWindowManagerStore((s) => s.setDesktopSize);
  // Aktivní edge-snap zóna (během tažení floating okna) → náhled cílové poloviny/čtvrtiny.
  const snapZone = useSnapStore((s) => s.zone);

  const actions = useWindowingActions();
  const { startDrag, startResize } = usePointerDrag();
  const pruneChrome = useLegacyWindowChromeStore((s) => s.prune);
  const chromeByLayer = useLegacyWindowChromeStore((s) => s.byLayer);

  // Naslouchá window/chrome + window/focus z legacy iframů (vycucnutí titulku/akcí + fokus).
  useLegacyWindowChrome();

  // Perzistence layoutu do URL fragmentu (boot čtení + debounced zápis). Mountnuto PŘED seedovacím
  // efektem floating rectů → kotva má přednost před fallback geometrií.
  useWindowLayoutUrlHash();

  const deskRef = useRef<HTMLDivElement | null>(null);
  // Při PRVNÍM běhu s okny (obnova z kotvy) clampujeme „celá vidět"; dál povolíme částečné přesahy.
  const restoreClampedRef = useRef(false);
  const [dim, setDim] = useState({ w: 1280, h: 640 });
  const [listW, setListW] = useState(DEFAULT_LIST_W);

  // Měření plochy.
  useLayoutEffect(() => {
    const node = deskRef.current;
    if (!node) return;
    const apply = () => {
      const size = { w: node.clientWidth, h: node.clientHeight };
      setDim(size);
      setDesktopSize(size);
    };
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    apply();
    return () => ro.disconnect();
  }, [setDesktopSize]);

  // Synchronizace WM UI stavu.
  const layerIds = useMemo(() => navigation.layers.map((l) => l.layerId), [navigation.layers]);
  const stackKeys = useMemo(
    () => Array.from(new Set(navigation.layers.map((l) => stackKeyOf(l.stackId)))),
    [navigation.layers]
  );
  useEffect(() => {
    ensureDefaults(layerIds);
    ensureStacks(stackKeys);
    prune(layerIds);
    pruneChrome(layerIds);
  }, [layerIds, stackKeys, ensureDefaults, ensureStacks, prune, pruneChrome]);

  const projection = useMemo(
    () =>
      deriveDesktopProjection({
        state: navigation,
        windows,
        activeLayerId,
        registry: kernel.getRegistry(),
        chromeByLayer,
      }),
    [navigation, windows, activeLayerId, kernel, chromeByLayer]
  );

  // Floating okna bez rectu v storu (po reloadu / po navigaci do existujícího stacku) naseedujeme:
  //  - okno přibyvší DO stacku (navigace z floating zdroje) → PŘEKRYJE celé okno pod ním
  //    (stejný rect, offset 0 — jako kaskáda na ploše),
  //  - jinak výchozí floating geometrie.
  // Běží jen pro chybějící rect → konverguje.
  //
  // DŮLEŽITÉ: rect čteme z ŽIVÉHO storu (getState), ne z React snapshotu `windows`. Boot-read
  // kotvy (useWindowLayoutUrlHash) běží ve stejném efekt-flushi TĚSNĚ PŘED tímto efektem a
  // aplikuje rect/mode přes setRects — closure `windows` je v tu chvíli ještě zastaralá (bez nich)
  // a seed by hash přepsal. getState() vidí čerstvý stav → hash-aplikovaná okna přeskočíme.
  useEffect(() => {
    const live = () => useWindowManagerStore.getState().windows;
    for (const group of projection.floatingGroups) {
      group.windows.forEach((w, i) => {
        if (w.mode === 'minimized' || live()[w.layerId]?.rect) return;
        let baseRect: WindowRect | undefined;
        for (let j = i - 1; j >= 0; j--) {
          const r = live()[group.windows[j]?.layerId ?? '']?.rect;
          if (r) {
            baseRect = r;
            break;
          }
        }
        setRect(w.layerId, baseRect ?? fallbackFloatingRect(dim.w, dim.h, w.withinIndex));
      });
    }

    // Dialog (legacy malé okno v ploše) při PRVNÍM výskytu naseedujeme geometrií:
    //  - zapamatovaná velikost z localStorage (per agenda) má přednost před legacy velikostí;
    //    pokud byl dialog naposled maximalizovaný, otevřeme ho rovnou maximalizovaný
    //    (ignorujeme tak legacy požadavek na zmenšení),
    //  - jinak vycentrujeme na legacy (chrome) velikost.
    // Drag/resize potřebuje výchozí rect ve storu. Jen když okno ještě nemá WM stav (rect).
    if (dim.w > 0 && dim.h > 0) {
      for (const w of projection.plocha) {
        if (!w.dialog || live()[w.layerId]?.rect) continue;
        const remembered = readWindowSizeMemory(w.layer);
        if (remembered) {
          setRect(w.layerId, centerRect(remembered.w, remembered.h, dim));
          if (remembered.maximized) useWindowManagerStore.getState().maximize(w.layerId);
        } else if (!dialogFillsDesktop(w.dialog, dim)) {
          setRect(w.layerId, centeredDialogRect(w.dialog, dim));
        }
      }
    }
  }, [projection, windows, dim, setRect]);

  // Drž okna v rozumné poloze (zapisuje do storu → aktualizuje kotvu). Klíčováno na
  // [dim, navigation.layers] → běží po reloadu (obnova z kotvy), při resize plochy a při open/close —
  // NE při tažení (to mění jen `windows`), takže drag neruší. Čte živý stav.
  //   - PRVNÍ běh s okny = obnova z kotvy → okna CELÁ vidět (full clamp, i zmenší),
  //   - dál = povolíme částečné přesahy, jen držíme viditelný titulek (loose clamp).
  useEffect(() => {
    if (dim.w <= 0 || dim.h <= 0 || navigation.layers.length === 0) return;
    const fullClamp = !restoreClampedRef.current;
    restoreClampedRef.current = true;
    const clampFn = fullClamp ? clampRectToViewport : clampRectKeepTitle;
    const live = useWindowManagerStore.getState().windows;
    for (const layer of navigation.layers) {
      const ui = live[layer.layerId];
      if (!ui?.rect || ui.mode === 'minimized' || ui.mode === 'maximized') continue;
      const clamped = clampFn(ui.rect, dim);
      const r = ui.rect;
      if (clamped.x !== r.x || clamped.y !== r.y || clamped.w !== r.w || clamped.h !== r.h) {
        setRect(layer.layerId, clamped);
      }
    }
  }, [dim, navigation.layers, setRect]);

  // Šířka list panelu — persistovaná per root agenda PLOCHY (sdíleno se starým splitterem).
  const rootAgendaId = useMemo(
    () =>
      navigation.layers.find((l) => l.stackId === undefined && l.presentation === 'root')?.agendaId,
    [navigation.layers]
  );
  useEffect(() => {
    if (!rootAgendaId) return;
    try {
      const v = localStorage.getItem(splitterStorageKey(rootAgendaId));
      if (v) setListW(Number(v));
    } catch {
      /* localStorage nedostupné */
    }
  }, [rootAgendaId]);

  const onSplitterDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startW = listW;
      const containerW = deskRef.current?.clientWidth ?? dim.w;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      let w = startW;
      const move = (ev: PointerEvent) => {
        w = Math.max(
          MIN_LIST_W,
          Math.min(startW + (ev.clientX - startX), containerW - MIN_DETAIL_W)
        );
        setListW(w);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        if (rootAgendaId) {
          try {
            localStorage.setItem(splitterStorageKey(rootAgendaId), String(w));
          } catch {
            /* localStorage nedostupné */
          }
        }
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [listW, dim.w, rootAgendaId]
  );

  const hasVisibleDetail = projection.isListDetail
    ? projection.plocha.some((w) => w.detailRole === 'detail')
    : false;

  return (
    <div
      className="pwm-app phx-root"
      data-hvariant="C"
      data-distinction="medium"
      style={{ '--pwm-anim-dur': '0.2s' } as React.CSSProperties}
    >
      <div className="pwm-desktop" ref={deskRef}>
        {/* Jeden seznam ve STABILNÍM lineárním pořadí — DOM se nepřeskupuje. */}
        {navigation.layers.map((layer) => {
          const win = projection.byLayerId.get(layer.layerId);
          if (!win) return null;
          const rect = computeRect(win, dim, listW);
          const zIndex = 10 + (stackZ[win.stackKey] ?? 0) * STACK_Z_SPAN + win.withinIndex;
          return (
            <DesktopWindowHost
              key={win.layerId}
              win={win}
              rect={rect}
              zIndex={zIndex}
              isRoot={win.stackId === undefined && win.withinIndex === 0}
              actions={actions}
              startDrag={startDrag}
              startResize={startResize}
              crumbNavigate={
                win.stackId === undefined ? (id) => void kernel.closeTo(id) : undefined
              }
            >
              {renderContent(win)}
            </DesktopWindowHost>
          );
        })}

        {/* Edge-snap náhled cílové poloviny/čtvrtiny (nad okny, neinteraktivní). */}
        {snapZone &&
          (() => {
            const r = snapRectForZone(snapZone, dim);
            return (
              <div
                className="pwm-snap-preview"
                style={{ left: r.x, top: r.y, width: r.w, height: r.h }}
                aria-hidden="true"
              >
                <span className="pwm-snap-preview-glyph">
                  <Icon name="dock" size={40} />
                </span>
              </div>
            );
          })()}

        {/* Splitter mezi list a detail panelem (jen list-detail s viditelným detailem). */}
        {projection.isListDetail && hasVisibleDetail && (
          <div
            className="pwm-splitter"
            style={{ left: listW - 3 }}
            onPointerDown={onSplitterDown}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

interface DesktopWindowHostProps {
  readonly win: DesktopWindow;
  readonly rect: WindowRect;
  readonly zIndex: number;
  readonly isRoot: boolean;
  readonly actions: ReturnType<typeof useWindowingActions>;
  readonly startDrag: ReturnType<typeof usePointerDrag>['startDrag'];
  readonly startResize: ReturnType<typeof usePointerDrag>['startResize'];
  readonly crumbNavigate?: (layerId: string) => void;
  readonly children: React.ReactNode;
}

/** Napojí jedno okno na akce/handlery podle jeho režimu a role. */
function DesktopWindowHost({
  win,
  rect,
  zIndex,
  isRoot,
  actions,
  startDrag,
  startResize,
  crumbNavigate,
  children,
}: DesktopWindowHostProps) {
  const id = win.layerId;
  const isMinimized = win.mode === 'minimized';
  const interactive = win.isInteractive && !win.hidden;
  const isFloating = win.mode === 'floating' || win.mode === 'maximized';
  const isMaximized = win.mode === 'maximized';
  const isListPane = win.detailRole === 'list';
  // Dialog = legacy malé okno v ploše. Smí se v ploše maximalizovat / zvětšovat / posouvat
  // (stejné gesto jako floating), aniž by se muselo osamostatnit.
  const isDialog = win.dialog !== undefined;
  const movable = isFloating || isDialog;
  const canDragResize = movable && !isMaximized;

  // Ovládání jen pro interaktivní okno. List panel nelze osamostatnit (jen zavřít).
  const controls = interactive
    ? {
        onDetach: isListPane || (isFloating && win.isBase) ? undefined : () => actions.detach(id),
        onDock: isFloating && win.isBase ? () => actions.dock(id) : undefined,
        // Dialog v ploše nemá minimalizaci — jen maximalizace/obnovení (+ osamostatnit/zavřít).
        onMinimize: isFloating && !isDialog ? () => actions.minimize(id) : undefined,
        onMaximize: movable && !isMaximized ? () => actions.maximize(id) : undefined,
        onRestore: isMaximized ? () => actions.restore(id) : undefined,
        onClose: () => actions.close(id),
      }
    : {};

  return (
    <PhoenixWindow
      win={win}
      rect={rect}
      zIndex={zIndex}
      isRoot={isRoot}
      hidden={isMinimized || win.hidden}
      chromeless={isListPane}
      onFocus={() => !win.hidden && actions.focus(id)}
      onCrumbNavigate={crumbNavigate}
      onHeaderPointerDown={
        interactive
          ? (e) => {
              actions.focus(id);
              if (canDragResize) startDrag(e, id);
            }
          : undefined
      }
      onHeaderDoubleClick={
        interactive && movable
          ? () => (isMaximized ? actions.restore(id) : actions.maximize(id))
          : undefined
      }
      onResizeStart={interactive && canDragResize ? (e, dir) => startResize(e, id, dir) : undefined}
      {...controls}
    >
      {children}
    </PhoenixWindow>
  );
}

/** Vycentruje obdélník dané (plné) velikosti do plochy. */
function centerRect(fullW: number, fullH: number, dim: { w: number; h: number }): WindowRect {
  const w = Math.min(fullW, dim.w);
  const h = Math.min(fullH, dim.h);
  return {
    x: Math.max(0, Math.round((dim.w - w) / 2)),
    y: Math.max(0, Math.round((dim.h - h) / 2)),
    w,
    h,
  };
}

/** Vycentrovaný rect dialogu na jeho intrinsic velikost (výška obsahu + hlavička). */
function centeredDialogRect(
  dialog: { w: number; h: number },
  dim: { w: number; h: number }
): WindowRect {
  return centerRect(dialog.w, dialog.h + WIN_HEADER_PX, dim);
}

/** Dialog vyplní (skoro) celou plochu → chovej se jako docked full (žádné tenké okraje). */
function dialogFillsDesktop(
  dialog: { w: number; h: number },
  dim: { w: number; h: number }
): boolean {
  return (
    Math.min(dialog.w, dim.w) >= dim.w - 1 && Math.min(dialog.h + WIN_HEADER_PX, dim.h) >= dim.h - 1
  );
}

/** Spočítá geometrii okna podle role (list-detail) / režimu (docked/floating). */
function computeRect(win: DesktopWindow, dim: { w: number; h: number }, listW: number): WindowRect {
  // Legacy dialog: po resize/move má vlastní rect ve storu (drží se ho, jen clampneme do plochy);
  // jinak vycentrovaný na intrinsic velikost. (Maximalizovaný dialog má mode 'maximized' → okno
  // se kreslí přes celou plochu v PhoenixWindow, tato hodnota se nepoužije.)
  if (win.dialog) {
    if (win.rect) return clampRectKeepTitle(win.rect, dim);
    if (dialogFillsDesktop(win.dialog, dim)) return dockedRect(0, dim.w, dim.h);
    return centeredDialogRect(win.dialog, dim);
  }
  switch (win.detailRole) {
    case 'list':
      return { x: 0, y: 0, w: Math.min(listW, dim.w), h: dim.h };
    case 'detail':
      return { x: listW, y: 0, w: Math.max(MIN_DETAIL_W, dim.w - listW), h: dim.h };
    case 'collapsed':
      return { x: 0, y: 0, w: dim.w, h: dim.h }; // skryté (display:none), geometrie nezáleží
    case 'overlay':
      return dockedRect(win.withinIndex, dim.w, dim.h);
    default:
      if (win.stackId === undefined) return dockedRect(win.withinIndex, dim.w, dim.h);
      // Floating: drž se uloženého rectu; smí přesahovat ven, jen drž viditelný titulek.
      return win.rect
        ? clampRectKeepTitle(win.rect, dim)
        : fallbackFloatingRect(dim.w, dim.h, win.withinIndex);
  }
}

function dockedRect(withinIndex: number, w: number, h: number): WindowRect {
  const off = withinIndex * DOCK_OFFSET_PX;
  return { x: off, y: off, w: Math.max(320, w - off), h: Math.max(200, h - off) };
}

function fallbackFloatingRect(w: number, h: number, offsetIndex = 0): WindowRect {
  const off = offsetIndex * 28;
  return {
    x: Math.min(Math.max(8, w - 360), 90 + off),
    y: Math.min(Math.max(8, h - 200), 60 + off),
    w: Math.round(w * 0.72),
    h: Math.round(h * 0.78),
  };
}

function renderContent(win: DesktopWindow) {
  if (win.status === 'error') return <NavigationErrorLayer layer={win.layer} />;
  if (win.status === 'lazy') return <NavigationLazyPlaceholder title={win.title} />;
  return <NavigationLayerHost layer={win.layer} isInteractive={win.isInteractive} />;
}
